import axios from "axios"
import { DateTime } from "luxon"

import { notificationsUrl } from "../../config/microservicesUrls"
import {
  isEnterpriseAdmin,
  isPartnerAdmin,
  PARTNER_ADMIN_ROLE,
} from "../../helpers/staffRoleHelpers"

const api_url = notificationsUrl()

async function axiosPut(jwt, endpoint, data) {
  const axiosObj = axios.create()

  const requestConfig = {
    headers: {
      Authorization: "Bearer " + jwt,
      "Content-Type": "application/json",
    },
  }

  const result = await axiosObj
    .put(`${api_url}/${endpoint}`, data, requestConfig)
    .then((responseBody) => {
      return responseBody.data
    })

  return result
}

async function axiosPost(jwt, endpoint, data) {
  const axiosObj = axios.create()

  const requestConfig = {
    headers: {
      Authorization: "Bearer " + jwt,
      "Content-Type": "application/json",
    },
  }

  let result = await axiosObj
    .post(`${api_url}/${endpoint}`, data, requestConfig)
    .then((responseBody) => {
      return responseBody.data
    })

  return result
}

async function axiosGet(jwt, endpoint) {
  const axiosObj = axios.create()

  const requestConfig = {
    headers: {
      Authorization: "Bearer " + jwt,
    },
  }

  let result = await axiosObj
    .get(`${api_url}/${endpoint}`, requestConfig)
    .then((responseBody) => {
      return responseBody.data
    })

  return result
}

//duplicating the axiosGet to avoid any impact on exiting features at this point
async function axiosPutWithCallBack(jwt, endpoint, data, callback) {
  const axiosObj = axios.create()

  const requestConfig = {
    headers: {
      Authorization: "Bearer " + jwt,
      "Content-Type": "application/json",
    },
  }

  const result = await axiosObj
    .put(`${api_url}/${endpoint}`, data, requestConfig)
    .then((responseBody) => {
      try {
        if (callback) {
          callback()
        }
      } catch (e) {
        console.error(e)
      }
      return responseBody.data
    })

  return result
}

export async function noteApiMarkAllAsReadPut(jwt, userCtx) {
  return await axiosPut(jwt, "Notification/MarkAllRead", {
    groupId: "All",
    userId: userCtx.session.user.staff_id,
    alternateUserId: userCtx.session.user.staff_alias || "0",
    alternateGroupId: userCtx.session.user.organization_alias,
  })
}

export async function noteApiMarkAllAsReadByParticipantPut(
  jwt,
  userCtx,
  participantId, //This is not necessarily the same as the context
  participantAlias,
  eventType,
) {
  return await axiosPut(jwt, "Notification/MarkAllRead/ByParticipant", {
    groupId: userCtx.session.user.organization_id,
    alternateGroupId: userCtx.session.user.organization_alias || "0",
    userId: userCtx.session.user.staff_id,
    alternateUserId: userCtx.session.user.staff_alias || "0",
    caseId: participantId,
    alternateCaseId: participantAlias || "0",
    eventType: eventType,
  })
}

export async function noteApiMarkAllSystemAsReadPut(jwt, userCtx) {
  return await axiosPut(jwt, "Notification/MarkAllRead/System", {
    enterpriseId: userCtx.session.user.enterprise_id || "0",
    alternateEnterpriseId: userCtx.session.user.enterprise_alias || "0",
    groupId: userCtx.session.user.organization_id,
    alternateGroupId: userCtx.session.user.organization_alias || "0",
    userId: userCtx.session.user.staff_id,
    alternateUserId: userCtx.session.user.staff_alias || "0",
  })
}

export async function noteApiMarkAsReadPut(jwt, notificationGuid) {
  return await axiosPut(jwt, "Notification/MarkRead", notificationGuid)
}

export async function noteApiMarkSystemAsReadPut(
  jwt,
  notificationGuid,
  userId,
) {
  return await axiosPut(jwt, "Notification/MarkRead/System", {
    notificationId: notificationGuid,
    userId: userId,
  })
}

export async function noteApiMarkAsUnreadPut(jwt, notificationGuid) {
  return await axiosPut(jwt, "Notification/MarkUnread", notificationGuid)
}

export async function noteApiMarkSystemAsUnreadPut(
  jwt,
  notificationGuid,
  userId,
) {
  return await axiosPut(jwt, "Notification/MarkUnread/System", {
    notificationId: notificationGuid,
    userId: userId,
  })
}

export async function noteApiCountPost(
  jwt,
  enterpriseId,
  enterpriseAlias,
  organization_id,
  organizationAlias,
  organizationIdList,
  staff_id,
  staff_alias,
  staff_creationDate,
) {
  return await axiosPost(jwt, "Notification/Count", {
    enterpriseId: enterpriseId || "0",
    alternateEnterpriseId: enterpriseAlias || "0",
    groupId: organization_id || "0",
    groupIds: organizationIdList,
    alternateGroupId: organizationAlias || "0",
    userId: staff_id,
    alternateUserId: staff_alias || "0",
    peerId: "0",
    caseId: "0",
    systemNotificationFilters: ["Staff", "All"],
    CreateDateStartFilter: staff_creationDate,
  })
}

export async function noteApiListMutedPost(jwt, organization_id, staff_id) {
  return await axiosPost(jwt, "Notification/Muted/List", {
    groupId: organization_id || "0",
    userId: staff_id,
  })
}

export async function noteApiMuteNotificationsPOST(
  jwt,
  organization_id,
  staff_id,
  eventTypes,
) {
  return await axiosPost(jwt, "Notification/Mute", {
    groupId: organization_id,
    userId: staff_id,
    eventTypes: eventTypes,
  })
}

export async function noteApiListNotificationsPOST(
  jwt,
  userCtx,
  organizationIdsFilter,
  readFilter,
  eventTypeFilter,
  createDateStartFilter,
  createDateEndFilter,
  readDateStartFilter,
  readDateEndFilter,
  titleFilter,
  sortOrder,
  pageNum,
  pageSize,
) {
  let organization_id =
    isEnterpriseAdmin(userCtx.session.user.staff_role, true) ||
    isPartnerAdmin(userCtx.session.user.staff_role)
      ? "All"
      : userCtx.session.user.organization_id

  let organization_alias =
    isEnterpriseAdmin(userCtx.session.user.staff_role, true) ||
    isPartnerAdmin(userCtx.session.user.staff_role)
      ? "All"
      : userCtx.session.user.organization_alias

  return await axiosPost(jwt, `Notification/List`, {
    groupId: organization_id,
    groupIds: organizationIdsFilter,
    userId: userCtx.session.user.staff_id,
    alternateGroupId: organization_alias,
    alternateUserId: userCtx.session.user.staff_alias,
    readFilter: readFilter,
    eventTypeFilter: eventTypeFilter,
    createDateStartFilter: createDateStartFilter
      ? DateTime.fromJSDate(createDateStartFilter).toUTC()
      : null,
    createDateEndFilter: createDateEndFilter
      ? DateTime.fromJSDate(createDateEndFilter).toUTC()
      : null,
    readDateStartFilter: readDateStartFilter
      ? DateTime.fromJSDate(readDateStartFilter).toUTC()
      : null,
    readDateEndFilter: readDateEndFilter
      ? DateTime.fromJSDate(readDateEndFilter).toUTC()
      : null,
    titleFilter: titleFilter,
    sortOrder: sortOrder,
    pageNum: pageNum,
    pageSize: pageSize,
  })
}

export async function noteApiListNotificationsSinceLastQueryPOST(
  userCtx,
  pageNum,
  pageSize,
) {
  let organization_id = userCtx.session.user.organization_id

  if (userCtx.session.user.staff_role === 9) {
    organization_id = "All"
  }

  return await axiosPost(
    userCtx.tokens.getNotificationToken(),
    `Notification/ListSinceLast`,
    {
      groupId: organization_id,
      userId: userCtx.session.user.staff_id,
      alternateGroupId: userCtx.session.user.organization_alias,
      alternateUserId: userCtx.session.user.staff_alias,
      pageNum: pageNum,
      pageSize: pageSize,
    },
  )
}

export async function noteApiListSystemNotificationsPOST(
  jwt,
  userCtx,
  readFilter,
  eventTypeFilter,
  createDateStartFilter,
  createDateEndFilter,
  readDateStartFilter,
  readDateEndFilter,
  titleFilter,
  sortOrder,
  pageNum,
  pageSize,
) {
  return await axiosPost(jwt, `Notification/List/System`, {
    enterpriseId: userCtx.session.user.enterprise_id || "0",
    alternateEnterpriseId: userCtx.session.user.enterprise_alias || "0",
    groupId: userCtx.session.user.organization_id,
    alternateGroupId: userCtx.session.user.organization_alias || "0",
    userId: userCtx.session.user.staff_id,
    alternateUserId: "0",
    readFilter: readFilter,
    eventTypeFilter: eventTypeFilter,
    createDateStartFilter: createDateStartFilter
      ? DateTime.fromJSDate(createDateStartFilter).toUTC()
      : null,
    createDateEndFilter: createDateEndFilter
      ? DateTime.fromJSDate(createDateEndFilter).toUTC()
      : null,
    readDateStartFilter: readDateStartFilter
      ? DateTime.fromJSDate(readDateStartFilter).toUTC()
      : null,
    readDateEndFilter: readDateEndFilter
      ? DateTime.fromJSDate(readDateEndFilter).toUTC()
      : null,
    titleFilter: titleFilter,
    systemNotificationFilters: ["Staff", "All"],
    sortOrder: sortOrder,
    pageNum: pageNum,
    pageSize: pageSize,
  })
}

export async function noteApiListEventTypesGet(jwt) {
  return await axiosGet(jwt, "Notification/List/EventTypes")
}

export async function noteApiUnmuteNotificationsGET(
  jwt,
  organization_id,
  user_id,
) {
  return await axiosGet(
    jwt,
    `Notification/Unmute/${organization_id}/${user_id}`,
  )
}

export async function noteApiCreateNote(jwt, note) {
  return await axiosPost(jwt, "Notification", note)
}

export async function noteApiSendToast(jwt, notificationId) {
  return await axiosPost(jwt, "Notification/SendSignalR", {
    notificationId: notificationId,
  })
}
